import { useQuery } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type { TrabajoDelColaborador, TrazaColaborador } from '@/lib/supabase/tipos-bd'

/**
 * Trazabilidad por colaborador.
 *
 * Sale de lo que la programación y la ejecución ya dejaron en la orden de
 * trabajo. No hay nada que digitar aquí.
 */

/** PostgREST devuelve los numeric como texto: se convierten en un solo sitio. */
function numero(valor: number | string | null): number | null {
  return valor === null ? null : Number(valor)
}

export function useTrazabilidad(dias: number | null) {
  const consulta = useQuery({
    queryKey: ['trazabilidad', dias],
    // Por si la conexión en vivo se cae sin avisar: cada minuto, solo.
    refetchInterval: 60 * 1000,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<TrazaColaborador[]> => {
      const { data, error } = await supabase.rpc('fn_trazabilidad_colaborador', {
        p_dias: dias,
      })

      if (error) throw new Error(mensajeDeError(error))

      return ((data ?? []) as unknown as TrazaColaborador[]).map((fila) => ({
        ...fila,
        horas_asignadas: Number(fila.horas_asignadas),
        horas_ejecutadas: Number(fila.horas_ejecutadas),
        horas_extra: Number(fila.horas_extra),
        cumplimiento_horas: numero(fila.cumplimiento_horas),
        cumplimiento_fecha: numero(fila.cumplimiento_fecha),
        retraso_mediana_horas: numero(fila.retraso_mediana_horas),
        retraso_maximo_horas: numero(fila.retraso_maximo_horas),
      }))
    },
  })

  return {
    colaboradores: consulta.data ?? [],
    cargando: consulta.isLoading,
    error: consulta.error?.message ?? null,
  }
}

/** Las órdenes de un colaborador. Una cifra sin su detalle no se puede discutir. */
export function useTrabajoDelColaborador(colaboradorId: string | null) {
  const consulta = useQuery({
    queryKey: ['trabajo-del-colaborador', colaboradorId],
    enabled: Boolean(colaboradorId),
    staleTime: 60 * 1000,
    queryFn: async (): Promise<TrabajoDelColaborador[]> => {
      const { data, error } = await supabase
        .from('vista_trabajo_del_colaborador')
        .select('*')
        .eq('colaborador_id', colaboradorId!)
        .order('fecha_inicio_programada', { ascending: false, nullsFirst: false })

      if (error) throw new Error(mensajeDeError(error))

      return (data ?? []) as unknown as TrabajoDelColaborador[]
    },
  })

  return { ordenes: consulta.data ?? [], cargando: consulta.isLoading }
}
